import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { Empty } from 'antd';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';

import { Select, Spin } from 'redpoints-front-components/index';
import { DEBOUNCE_INTERVAL } from 'redpoints-front-common/config';

import {
  allIncidentsFiltersClearDomains,
  allIncidentsFiltersFetchDomains,
  allIncidentsFiltersSetDomains,
} from '../../../actions/allIncidentsActions';
import {
  getAllIncidentsDomainsIsLoading,
  getAllIncidentsDomainsResponse,
  getAllIncidentsFiltersDomainsSelected,
} from '../../../reducers/reporting/allIncidents';
import { MIN_NUMBER_CHARACTERS } from '../../../config/constants';
import { translate } from '../../../helpers/languageHelper';
import { MAX_NUMBER_RESULTS } from './constants';

const contentMessagesStates = {
  EXCESS: 'EXCESS',
  INITIAL: 'INITIAL',
  LOADING: 'LOADING',
  NO_DATA: 'NO_DATA',
};

class AllIncidentsFiltersDomain extends PureComponent {
  static returnDomainFilters(domainsSelected, domainsDataResponse) {
    return domainsSelected
      ? domainsDataResponse.filter(domain => domain.key === domainsSelected)
      : [];
  }

  constructor(props) {
    super(props);
    this.clearSearchDomainFilter = this.clearSearchDomainFilter.bind(this);
    this.handleChangeDomain = this.handleChangeDomain.bind(this);
    this.renderNotFoundContent = this.renderNotFoundContent.bind(this);
    this.searchDomainFilter = '';
    this.searchDomainSelector = debounce(
      this.searchDomainSelector.bind(this),
      DEBOUNCE_INTERVAL
    );

    this.state = {
      messageNoContent: contentMessagesStates.INITIAL,
    };
  }

  componentDidUpdate(prevProps) {
    const { domainsDataResponse } = this.props;
    if (!isEqual(domainsDataResponse, prevProps.domainsDataResponse)) {
      if (domainsDataResponse.length > MAX_NUMBER_RESULTS) {
        this.setState({
          messageNoContent: contentMessagesStates.EXCESS,
        });
      } else if (
        !domainsDataResponse.length &&
        this.state.messageNoContent === contentMessagesStates.LOADING
      ) {
        this.setState({
          messageNoContent: contentMessagesStates.NO_DATA,
        });
      }
    } else if (
      !this.props.domainsIsLoading &&
      !domainsDataResponse.length &&
      this.searchDomainFilter.length >= MIN_NUMBER_CHARACTERS
    ) {
      this.setState({
        messageNoContent: contentMessagesStates.NO_DATA,
      });
    }
  }

  searchDomainSelector(value) {
    const {
      allIncidentsFiltersClearDomains,
      allIncidentsFiltersFetchDomains,
      allIncidentsFiltersSetDomains,
    } = this.props;
    this.searchDomainFilter = value;

    if (value.length >= MIN_NUMBER_CHARACTERS) {
      allIncidentsFiltersFetchDomains({ url: value });
      this.setState({
        messageNoContent: contentMessagesStates.LOADING,
      });
    } else {
      this.setState({
        messageNoContent: contentMessagesStates.INITIAL,
      });
      allIncidentsFiltersClearDomains();
      allIncidentsFiltersSetDomains({ domainId: null });
    }
  }

  handleChangeDomain(value) {
    const {
      allIncidentsFiltersClearDomains,
      allIncidentsFiltersSetDomains,
    } = this.props;
    allIncidentsFiltersSetDomains({ domainId: value });
    this.setState({
      messageNoContent: contentMessagesStates.INITIAL,
    });
    if (!value) {
      allIncidentsFiltersClearDomains();
    }
  }

  clearSearchDomainFilter() {
    this.searchDomainFilter = '';
  }

  renderNotFoundContent = () => {
    switch (this.state.messageNoContent) {
      case contentMessagesStates.LOADING:
        return <Spin size="small" />;
      case contentMessagesStates.NO_DATA:
        return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />;
      case contentMessagesStates.INITIAL:
        return <div>{translate('general_text.type_to_filter')}</div>;
      case contentMessagesStates.EXCESS:
        return (
          <div>
            {translate('reporting.allIncidentsInternal.filters.tooManyDomains')}
          </div>
        );
      default:
        return null;
    }
  };

  render() {
    const { domainsDataResponse, domainsSelected } = this.props;

    let domainData = [];
    const tooManyDomains = domainsDataResponse.length > MAX_NUMBER_RESULTS;
    if (tooManyDomains) {
      domainData = AllIncidentsFiltersDomain.returnDomainFilters(
        domainsSelected,
        domainsDataResponse
      );
    } else {
      domainData =
        this.searchDomainFilter.length >= MIN_NUMBER_CHARACTERS
          ? domainsDataResponse
          : AllIncidentsFiltersDomain.returnDomainFilters(
              domainsSelected,
              domainsDataResponse
            );
    }

    const sortedDomainData = domainData.sort((a, b) =>
      a.title.localeCompare(b.title)
    );

    return (
      <Select
        allowClear
        data={sortedDomainData}
        label={translate('reporting.allIncidentsInternal.filters.domain')}
        mapping={{ key: 'key', value: 'title' }}
        onChange={this.handleChangeDomain}
        onSearch={this.searchDomainSelector}
        onBlur={this.clearSearchDomainFilter}
        filterOption={false}
        placeholder="All"
        showSearch
        value={
          domainsSelected && domainData.length ? domainsSelected : undefined
        }
        notFoundContent={this.renderNotFoundContent()}
      />
    );
  }
}

const mapStateToProps = state => ({
  domainsDataResponse: getAllIncidentsDomainsResponse(state),
  domainsIsLoading: getAllIncidentsDomainsIsLoading(state),
  domainsSelected: getAllIncidentsFiltersDomainsSelected(state),
});

export default connect(mapStateToProps, {
  allIncidentsFiltersClearDomains,
  allIncidentsFiltersFetchDomains,
  allIncidentsFiltersSetDomains,
})(AllIncidentsFiltersDomain);
